/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Current Search Reducer
 */

import { ZOOM_DEFAULT, ZOOM_STATE } from '@zcode/nr-utils'
import { DEFAULT_LISTABLES } from '@zcode/nr/es/shared/bizos/listables'
import { MinCitiesType } from '@zcode/nr/es/shared/bizos/min_cities'
import nearbyHoodsReducer, {
  whitelist as nearbyHoodsAllowedList,
} from '@zcode/nr/es/shared/modules/nearbyHoods'
import { filterActions } from '@zumper/redux-ignore'
import reduceReducers from 'reduce-reducers'
import {
  CLEAR_CITIES,
  CLEAR_HOOD,
  CURRENT_SEARCH_MHOOD,
  GET_CITIES_SUCCESS,
  GET_HOODS_SUCCESS,
  GET_LISTABLES_COUNTS_BY_SUCCESS,
  GET_LISTABLES_SUCCESS,
  GET_MCITIES_SUCCESS,
  RESET_CLUSTERS_AND_LISTABLES,
  RESET_LISTABLES,
  RESET_MCITIES,
  SET_LAST_AUTOCOMPLETE_METRO,
  UPDATE_LOCATION,
} from '../actions/constants'
import { logger } from '../logger'
import {
  cityLifestylesAllowedList,
  cityLifestylesReducer,
} from '../modules/cityLifestyles/reducers'
import {
  contentModuleAmenitiesAllowedList,
  contentModuleAmenitiesReducer,
} from '../modules/contentModuleAmenities/reducers'
import {
  CLEAR_CITY_GUIDE_CITY_DATA,
  CLEAR_CITY_GUIDE_NEIGHBORHOOD_DATA,
  ERROR_FETCHING_CITY_GUIDE_CITY_DATA,
  ERROR_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA,
  HAS_MORE_LISTABLES,
  NO_MORE_LISTABLES,
  RECEIVE_CATEGORY_LISTABLES,
  RECEIVE_CITY_GUIDE_CITY_DATA,
  RECEIVE_CITY_GUIDE_NEIGHBORHOOD_DATA,
  RECEIVE_PAGINATED_IDS,
  SET_FIRST_PAGE_ADDITIONAL_COUNT,
  SET_FIRST_PAGE_COUNT,
  SET_FIRST_PAGE_OFFSET,
  SET_FROM_PAID_MARKETING,
  SET_HOOD_VERTICALS,
  SET_IS_CATEGORIES_LOADING,
  SET_IS_NAVBAR_EXPANDED,
  SET_LAST_CLICKED_ENTITY,
  SET_ROUTE_PATHNAME,
  SET_ROUTE_SEARCHSTRING,
  START_FETCHING_CITY_GUIDE_CITY_DATA,
  START_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA,
  UPDATE_ADDITIONAL_BUILDING_DATA,
} from '../modules/currentSearch/constants/index'
import {
  allowedList as hoodLifestylesAllowedList,
  hoodLifestylesReducer,
} from '../modules/currentSearch/modules/hoodLifestyles/reducers'
import {
  allowedList as locationAllowedList,
  locationReducer,
} from '../modules/currentSearch/modules/location/index'
import {
  EMPTY_LOCATION,
  Location,
} from '../modules/currentSearch/modules/location/schemas/location'
import {
  allowedList as rentResearchAllowedList,
  rentResearchReducer,
} from '../modules/currentSearch/modules/rentResearch/reducers/rentResearch'
import { allowedList as currentSearchAllowedList } from '../modules/currentSearch/reducers/index'
import { sweetDealsAllowedList, sweetDealsReducer } from '../modules/sweetDeals/reducers'

/**
 * Creates a reducer that listens to a single key. Useful with reduceReducers to combine an existing
 * reducer (like currentSearch) with a child reducer (like location).
 *
 * @template {string} Key
 * @template {unknown} Result
 * @template {{ type: string }} Actions
 * @param {(state: Result | undefined, action: Actions) => Result} reducer
 * @param {Key} key
 * @returns {<Obj>(state: Obj & {[P in Key]: Result} | undefined, action: Actions) => Obj & {[P in Key]: Result}}
 */
const createSubReducer = (reducer, key) => {
  return (state, action) => {
    const subState = state[key]
    const newSubState = reducer(subState, action)
    if (subState !== newSubState) {
      return {
        ...state,
        [key]: newSubState,
      }
    }
    return state
  }
}

// NOTE: here we are creating sub-reducers to manage only the location and allNearbyHoods keys.
// Below we are combining it with the main currentSearch reducer using reduceReducers. The idea here
// is we can manage the location portion of currentSearch in its own reducer without needing to
// completely re-write the currentSearch reducer. This allows us to create new modules for sub
// sections of this state tree one piece at a time.
const currentSearchLocation = createSubReducer(locationReducer, 'location')
const currentSearchNearbyHoods = createSubReducer(nearbyHoodsReducer, 'allNearbyHoods')
const currentSearchHoodLifestyles = createSubReducer(hoodLifestylesReducer, 'hoodLifestyles')
const currentSearchRentResearch = createSubReducer(rentResearchReducer, 'rentalData')
const currentSearchCityLifestyles = createSubReducer(cityLifestylesReducer, 'cityLifestyles')
const currentSearchContentModuleAmenities = createSubReducer(
  contentModuleAmenitiesReducer,
  'contentModuleAmenities'
)
const currentSearchSweetDeals = createSubReducer(sweetDealsReducer, 'sweetDeals')

const allowedList = [
  GET_LISTABLES_SUCCESS,
  RESET_LISTABLES,
  GET_CITIES_SUCCESS,
  GET_HOODS_SUCCESS,
  CLEAR_CITIES,
  CLEAR_HOOD,
  GET_MCITIES_SUCCESS,
  RESET_MCITIES,
  CURRENT_SEARCH_MHOOD,
  UPDATE_LOCATION,
  GET_LISTABLES_COUNTS_BY_SUCCESS,
  HAS_MORE_LISTABLES,
  NO_MORE_LISTABLES,
  SET_FROM_PAID_MARKETING,
  SET_HOOD_VERTICALS,
  SET_LAST_AUTOCOMPLETE_METRO,
]

const updateAdditionalBuildingDataState = (state, payload) => {
  if (!payload) {
    return state
  }
  const { buildingId, petPolicy, rentSpecialInfo } = payload
  if (!buildingId) {
    return state
  }
  const newObject = {
    ...payload,
  }
  if (petPolicy) {
    newObject.petPolicy = JSON.parse(petPolicy)
  }
  if (rentSpecialInfo) {
    newObject.rentSpecialInfo = JSON.parse(rentSpecialInfo)
  }

  return {
    ...state,
    additionalBuildingData: { ...state.additionalBuildingData, [buildingId]: newObject },
  }
}

/**
 *
 * @param {Omit<import('@zcode/state-type').CurrentSearch, 'allNearbyHoods' | 'location'> | undefined} state
 * @param {*} action
 * @returns {Omit<import('@zcode/state-type').CurrentSearch, 'allNearbyHoods' | 'location'>}
 */
const currentSearch = (
  // biome-ignore lint/style/useDefaultParameterLast: <explanation>
  state = {
    additionalBuildingData: {},
    categoryListables: {},
    cityGuideCity: {
      data: {},
      meta: {},
    },
    cityGuideNeighborhood: { data: {}, meta: {} },
    clusters: { clusters: [], clusterItems: [], neighborhoodIds: [] },
    countsBy: { bedrooms: {}, minPrice: {} },
    firstPageAdditionalCount: 0,
    firstPageCount: null,
    firstPageOffset: null,
    hasMoreListables: true,
    isCategoriesLoading: false,
    isFromPaidMarketing: false,
    isNavbarExpanded: false,
    lastClickedEntity: {},
    listables: {
      featured: [],
      jobId: 0,
      listables: [],
      matching: 0,
      nearby: null,
      query: null,
      spotlight: [],
    },
    minCities: {
      jobId: 0,
      listingCount: 0,
      listingCounts: {},
      minCities: [],
      query: null,
      zoom: null,
    },
    paginatedIds: [],
    pathname: '',
    searchString: '',
    saveSearchLocation: {
      box: { minLng: null, maxLng: null, minLat: null, maxLat: null },
      lat: null,
      lng: null,
      url: null,
      zoom: ZOOM_DEFAULT,
      hoodName: '',
      cityName: '',
      stateName: '',
      fromAutocomplete: false,
      fromBackButton: false,
      fromClient: false,
      fromLocateMe: false,
      fromCluster: false,
      fromMapInteraction: false,
      fromNoListables: false,
      missing: false,
      preventUrlUpdate: false,
    },
    sweetDeals: {},
    verticals: {},
  },
  action
) => {
  switch (action.type) {
    // LISTABLES
    case GET_LISTABLES_SUCCESS: {
      const { excludeGroupIds, listables } = action

      // We can simply replace the listables where there isn't an excludeGroupIds
      if (!excludeGroupIds) {
        return {
          ...state,
          listables,
        }
      }

      // Preserve meta data (matching, listingCount, etc.) if there's an excludeGroupIds for pagination
      return {
        ...state,
        listables: {
          ...state.listables,
          listables: listables.listables,
          featured: listables.featured,
          query: listables.query,
        },
      }
    }
    case UPDATE_ADDITIONAL_BUILDING_DATA:
      return updateAdditionalBuildingDataState(state, action.payload)
    case RESET_LISTABLES: {
      return {
        ...state,
        listables: DEFAULT_LISTABLES,
      }
    }
    // CLUSTERS
    case RESET_CLUSTERS_AND_LISTABLES: {
      return {
        ...state,
        listables: DEFAULT_LISTABLES,
      }
    }
    case GET_CITIES_SUCCESS: {
      if (action.city) {
        return {
          ...state,
          city: action.city,
        }
      }
      return state
    }
    case GET_HOODS_SUCCESS: {
      if (action.hood) {
        return {
          ...state,
          hood: action.hood,
        }
      }
      return state
    }
    case CLEAR_HOOD: {
      return {
        ...state,
        hood: undefined,
      }
    }
    case CLEAR_CITIES: {
      return {
        ...state,
        city: {},
      }
    }
    case GET_MCITIES_SUCCESS: {
      const { minCities, jobId, zoom } = action
      return {
        ...state,
        minCities: {
          ...MinCitiesType,
          jobId,
          minCities: minCities
            .slice()
            .sort((a, b) => b.listingCount - a.listingCount) // force a logical sort
            .slice(0, minCities.length >= 30 ? 30 : minCities.length), // trim the results to 30 (10 rows of 3)
          zoom,
        },
      }
    }
    case RESET_MCITIES: {
      return {
        ...state,
        minCities: MinCitiesType,
      }
    }
    case CURRENT_SEARCH_MHOOD: {
      return {
        ...state,
        hood: action.hood,
      }
    }

    // NOTE: here we are now using the location schema from the new location module. The new schema
    // does not include the following fields:
    // "fromAutocomplete", "fromBackButton", "fromClient", "fromLocateMe", "fromCluster",
    // "fromMapInteraction", "fromNoListables", "preventUrlUpdate",
    // "missing"
    // TODO: stop using this action here. Move all of this to the new location reducer.
    case UPDATE_LOCATION: {
      const { location, reset } = action
      if (!location) {
        return {
          ...state,
          location: EMPTY_LOCATION,
        }
      }

      const { url, box, missing, zoom, fromCluster } = location

      /**
       * Check to see if `url` is incorrectly missing from location schema.
       * This logic is to help us to catch these hard-to-test bugs related to location.
       * Only time we shouldn't care about undefined `url` is in the case of:
       * 1. box only search
       * 2. state-level zoom
       * 3. from cluster
       */
      if (!url && !missing && !box && zoom > ZOOM_STATE && !fromCluster) {
        logger.error('Missing url in location: ', location)
      }
      if (reset) {
        return {
          ...state,
          location: Location.fromJS(location),
        }
      }
      return {
        ...state,
        location: Location.fromJS({ ...state.location, ...location }),
      }
    }
    case GET_LISTABLES_COUNTS_BY_SUCCESS: {
      if (action?.countsBy) {
        return {
          ...state,
          countsBy: action?.countsBy,
        }
      }

      if (action?.payload) {
        return {
          ...state,
          countsBy: action.payload,
        }
      }
      return state
    }
    case HAS_MORE_LISTABLES: {
      return {
        ...state,
        hasMoreListables: true,
      }
    }
    case NO_MORE_LISTABLES: {
      return {
        ...state,
        hasMoreListables: false,
      }
    }
    case SET_FROM_PAID_MARKETING: {
      return {
        ...state,
        isFromPaidMarketing: action?.payload ?? false,
      }
    }
    case RECEIVE_PAGINATED_IDS: {
      return {
        ...state,
        paginatedIds: action?.payload ?? [],
      }
    }
    case SET_FIRST_PAGE_ADDITIONAL_COUNT: {
      return {
        ...state,
        firstPageAdditionalCount: action?.payload ?? 0,
      }
    }
    case SET_FIRST_PAGE_COUNT: {
      return {
        ...state,
        firstPageCount: action?.payload ?? null,
      }
    }
    case SET_FIRST_PAGE_OFFSET: {
      return {
        ...state,
        firstPageOffset: action?.payload ?? null,
      }
    }
    case SET_HOOD_VERTICALS: {
      return {
        ...state,
        verticals: { ...state.verticals, hood: action.payload ?? null },
      }
    }
    case SET_IS_NAVBAR_EXPANDED: {
      return {
        ...state,
        isNavbarExpanded: action.payload ?? false,
      }
    }
    case RECEIVE_CATEGORY_LISTABLES: {
      return {
        ...state,
        categoryListables: action.payload ?? {},
      }
    }
    case SET_IS_CATEGORIES_LOADING: {
      return {
        ...state,
        isCategoriesLoading: action.payload ?? {},
      }
    }
    case SET_ROUTE_PATHNAME: {
      return { ...state, pathname: action.payload ?? '' }
    }
    case SET_ROUTE_SEARCHSTRING: {
      return { ...state, searchString: action.payload ?? '' }
    }
    case SET_LAST_CLICKED_ENTITY: {
      return {
        ...state,
        lastClickedEntity: action.payload ?? {},
      }
    }
    case RECEIVE_CITY_GUIDE_CITY_DATA: {
      return {
        ...state,
        cityGuideCity: {
          data: action.payload,
          meta: { ...state.cityGuideCity.meta, fetching: false },
        },
      }
    }
    case CLEAR_CITY_GUIDE_CITY_DATA: {
      return {
        ...state,
        cityGuideCity: {
          data: {},
          meta: {},
        },
      }
    }
    case START_FETCHING_CITY_GUIDE_CITY_DATA: {
      return {
        ...state,
        cityGuideCity: {
          data: {},
          meta: { ...state.cityGuideCity.meta, fetching: true, key: action.payload },
        },
      }
    }
    case ERROR_FETCHING_CITY_GUIDE_CITY_DATA: {
      return {
        ...state,
        cityGuideCity: {
          ...state.cityGuideCity,
          meta: { ...state.cityGuideCity.meta, fetching: false, error: action.payload },
        },
      }
    }
    case RECEIVE_CITY_GUIDE_NEIGHBORHOOD_DATA: {
      return {
        ...state,
        cityGuideNeighborhood: {
          data: action.payload,
          meta: { ...state.cityGuideNeighborhood.meta, fetching: false },
        },
      }
    }
    case CLEAR_CITY_GUIDE_NEIGHBORHOOD_DATA: {
      return {
        ...state,
        cityGuideNeighborhood: {
          data: {},
          meta: {},
        },
      }
    }
    case START_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA: {
      return {
        ...state,
        cityGuideNeighborhood: {
          data: {},
          meta: { ...state.cityGuideNeighborhood.meta, fetching: true, key: action.payload },
        },
      }
    }
    case ERROR_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA: {
      return {
        ...state,
        cityGuideNeighborhood: {
          ...state.cityGuideNeighborhood,
          meta: { ...state.cityGuideNeighborhood.meta, fetching: false, error: action.payload },
        },
      }
    }
    default:
      return state
  }
}

export default filterActions(
  reduceReducers(
    currentSearch,
    currentSearchCityLifestyles,
    currentSearchContentModuleAmenities,
    currentSearchHoodLifestyles,
    currentSearchLocation,
    currentSearchNearbyHoods,
    currentSearchRentResearch,
    currentSearchSweetDeals,
    currentSearch(undefined, { type: 'init' })
  ),
  allowedList.concat(
    currentSearchAllowedList,
    cityLifestylesAllowedList,
    contentModuleAmenitiesAllowedList,
    hoodLifestylesAllowedList,
    locationAllowedList,
    nearbyHoodsAllowedList,
    rentResearchAllowedList,
    sweetDealsAllowedList
  )
)
